import React, { useEffect, useState, useMemo } from "react";
import { Box, Card, CardContent, Grid, Typography, MenuItem, Button, } from "@mui/material";
import { Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Area, AreaChart, } from "recharts";
import { getFailedTransactionReportAction } from "../../Actions/ReportsAction";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import BadgeIcon from "@mui/icons-material/Badge";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";

const PORTAL_FILTER_OPTIONS = [
    { label: "All Portals", value: "all" },
    { label: "QBot", value: "qbot" },
    { label: "PayCentral", value: "paycentral" },
    { label: "KYC", value: "kyc" },
    { label: "Tripola", value: "tripola" },
];

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "select",
        defaultValue: "all",
        options: PORTAL_FILTER_OPTIONS,
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const FailedTransactionReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [transactions, setTransactions] = useState([]);

    const buildApiFilters = (fv) => ({
        portal: fv.portal === "all" ? "" : fv.portal,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchReport = async (fv = filterValues) => {
        setLoading(true);
        try {
            const response = await getFailedTransactionReportAction(buildApiFilters(fv));
            const reportData = response?.data;
            setTransactions(reportData?.transactions || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchReport(resetValues);
    };

    const hasAppliedFilters = filterValues.portal !== "all";

    const totalFailures = transactions.length;

    const failedAmount = transactions.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
    );

    const portalCount = {};
    const portalAmount = {};
    const dailyFailures = {};

    transactions.forEach((item) => {
        const portal = item.portal || "Unknown";
        portalCount[portal] = (portalCount[portal] || 0) + 1;
        portalAmount[portal] = (portalAmount[portal] || 0) + Number(item.amount || 0);
        const date = new Date(item.transactionDate).toLocaleDateString();
        dailyFailures[date] = (dailyFailures[date] || 0) + 1;
    });

    const portalChartData = Object.keys(portalCount).map((key) => ({
        name: key,
        value: portalCount[key],
    }));

    const portalAmountChartData = Object.keys(portalAmount).map((key) => ({
        name: key,
        value: Number(portalAmount[key].toFixed(2)),
    }));

    const dailyFailureChartData = Object.keys(dailyFailures).map((key) => ({
        date: key,
        count: dailyFailures[key],
    }));

    const qbotFailures = portalCount["QBot"] || 0;
    const paycentralFailures = portalCount["PayCentral"] || 0;
    const kycFailures = portalCount["KYC"] || 0;
    const tripolaFailures = portalCount["Tripola"] || 0;

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">
                                        Failed Transaction Report
                                    </h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* KPI Cards */}
                <Grid item xs={12} md={2}>
                    <Card sx={{ bgcolor: "#fef2f2", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <ErrorOutlineIcon color="error" />
                                <Typography variant="h6">
                                    Total Failures
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {totalFailures}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Card sx={{ bgcolor: "#fff7ed", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AttachMoneyIcon sx={{ color: "#ea580c" }} />
                                <Typography variant="h6">
                                    Failed Amount
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {failedAmount.toFixed(2)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Card sx={{ bgcolor: "#eef4ff", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <SmartToyIcon sx={{ color: "#2563eb" }} />
                                <Typography variant="h6">
                                    QBot Failures
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {qbotFailures}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Card sx={{ bgcolor: "#ecfdf5", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AttachMoneyIcon sx={{ color: "#16a34a" }} />
                                <Typography variant="h6">
                                    PayCentral Failures
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {paycentralFailures}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Card sx={{ bgcolor: "#f5f3ff", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <BadgeIcon sx={{ color: "#7c3aed" }} />
                                <Typography variant="h6">
                                    KYC Failures
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {kycFailures}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={2}>
                    <Card sx={{ bgcolor: "#ecfeff", boxShadow: 2 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1}>
                                <TravelExploreIcon sx={{ color: "#0891b2" }} />
                                <Typography variant="h6">
                                    Tripola Failures
                                </Typography>
                            </Box>
                            <Typography variant="h4">
                                {tripolaFailures}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0, }}>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden", }}>
                            <CardContent>
                                <Typography variant="h6" mb={2}>
                                    Failures By Portal
                                </Typography>
                                <Box height={350}>
                                    <ResponsiveContainer>
                                        <BarChart data={portalChartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value">
                                                {portalChartData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={
                                                            entry.name === "QBot"
                                                                ? "#22419e"
                                                                : entry.name === "PayCentral"
                                                                    ? "#16a34a"
                                                                    : entry.name === "KYC"
                                                                        ? "#8b5cf6"
                                                                        : entry.name === "Tripola"
                                                                            ? "#f97316"
                                                                            : "#94a3b8"
                                                        }
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden", }}>
                            <CardContent>
                                <Typography variant="h6" mb={2}>
                                    Failed Amount By Portal
                                </Typography>
                                <Box height={350}>
                                    <ResponsiveContainer>
                                        <BarChart data={portalAmountChartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value">
                                                {portalAmountChartData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={
                                                            entry.name === "QBot"
                                                                ? "#3b82f6"
                                                                : entry.name === "PayCentral"
                                                                    ? "#22c55e"
                                                                    : entry.name === "KYC"
                                                                        ? "#a855f7"
                                                                        : entry.name === "Tripola"
                                                                            ? "#f59e0b"
                                                                            : "#94a3b8"
                                                        }
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden", }}>
                            <CardContent>
                                <Typography variant="h6" mb={2}>
                                    Daily Failure Trend
                                </Typography>
                                <Box height={350}>
                                    <ResponsiveContainer>
                                        <AreaChart data={dailyFailureChartData}>
                                            <CartesianGrid stroke="#e5e7eb" vertical={false} />
                                            <XAxis
                                                dataKey="date"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: "#6b7280", fontSize: 12 }}
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: "#6b7280", fontSize: 12 }}
                                            />
                                            <Tooltip />
                                            <Area
                                                type="linear"
                                                dataKey="count"
                                                stroke="#22419e"
                                                fill="rgba(34, 65, 158, 0.15)"
                                                strokeWidth={3}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default FailedTransactionReport;
